import { useEffect, useState } from 'react';
import VisibleLoading from '@/components/utilities/VisibleLoading';

// components
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import Link from '@/components/utilities/SmartLink';
import { Icon } from '@/components/ui/icon';
import { Slider } from '@/components/ui/slider';

// utilities
import { getImagePath } from '@/utils/images';
import { formatCurrency } from '@/utils/formatting';

const YEAR = 'year';

const PricingBlock = ({ defaultType, types, pricing, pricingContent: content, currency }) => {
  // Use props directly as initial state, avoiding unnecessary effect
  const initialPeriod = defaultType || types?.[0]?.type || YEAR;
  const [period, setPeriod] = useState(initialPeriod);
  const [exchangeRate, setExchangeRate] = useState(null);

  const needsConversion = currency && currency !== 'CZK';

  useEffect(() => {
    if (!needsConversion) return;
    fetch(`/api/exchange-rate?currency=${currency}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => setExchangeRate(data))
      .catch(() => setExchangeRate(null));
  }, [needsConversion, currency]);

  const effectiveCurrency = needsConversion && exchangeRate ? currency : 'CZK';
  const convertPrice = (priceCzk) => {
    if (!needsConversion || !exchangeRate || priceCzk == null) return priceCzk;
    return priceCzk / (exchangeRate.rate / exchangeRate.amount);
  };

  return (
    <div className="mx-auto my-8 w-[90vw] lg:mt-16 xl:w-(--container-width)">
      <VisibleLoading delay={150}>
        {({ ref, className }) => (
          <div
            ref={ref}
            className={`bg-grey-50 mx-auto mb-6 flex w-fit gap-1 rounded-full p-1 font-medium text-black ${className || ''}`.trim()}
          >
            {(types || []).map((type) => (
              <div
                key={type.id}
                onClick={() => setPeriod(type.type)}
                className={`ease cursor-pointer rounded-full border border-black px-4 py-1.5 text-center transition-colors duration-300 ${period === type.type ? '' : 'border-transparent'}`}
              >
                {type.label}
              </div>
            ))}
          </div>
        )}
      </VisibleLoading>

      <div className="flex flex-wrap items-stretch justify-center gap-y-6">
        {(content || []).map((card, index) => (
          <VisibleLoading key={card.id} delay={(index + 1) * 100}>
            {({ ref, className }) => (
              <PricingCard
                innerRef={ref}
                className={className}
                card={card}
                pricing={pricing}
                period={period}
                currency={effectiveCurrency}
                convertPrice={convertPrice}
              />
            )}
          </VisibleLoading>
        ))}
      </div>
    </div>
  );
};
export default PricingBlock;

const PricingCard = ({ innerRef, className, card, period, pricing, currency, convertPrice }) => {
  return (
    <div ref={innerRef} className={`flex xl:basis-1/3 xl:px-3 ${className || ''}`.trim()}>
      <div
        className={`flex flex-col overflow-hidden rounded-2xl border-2 text-center shadow-2xl/15 ${card.popular ? 'border-green-500' : 'border-transparent'}`}
      >
        <div
          className={`txt-lg flex h-10 items-center justify-center font-medium text-white ${card.popular ? 'bg-green-500' : 'bg-transparent'}`}
        >
          {card.popular ? <span>{card.popularLabel}</span> : null}
        </div>
        <div className="flex flex-1 flex-col px-10 pt-6">
          <div className="mb-6 h-12 text-center">
            {card?.icon && (
              <Image
                src={getImagePath(card.icon)}
                width={card.icon.width}
                height={card.icon.height}
                alt={card.title}
                sizes="48px"
                className="mx-auto h-12 w-12 object-contain"
              />
            )}
          </div>
          <h2>{card.title}</h2>
          <div className="txt-md text-brand-400 mb-4 font-medium">{card.subtitle}</div>
          <div className="txt-lg mb-8 min-h-[60px] font-medium">{card.text}</div>
          <div className="flex min-h-[190px] flex-col gap-4 pb-6">
            {card.features.map((feature, index) => (
              <div key={index} className="text-grey-950 flex items-center gap-3 text-left">
                {feature.checked ? (
                  <svg
                    className="min-w-6"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="24" height="24" rx="12" fill="#D1FADF" />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M17.098 7.39016L9.93797 14.3002L8.03797 12.2702C7.68797 11.9402 7.13797 11.9202 6.73797 12.2002C6.34797 12.4902 6.23797 13.0002 6.47797 13.4102L8.72797 17.0702C8.94797 17.4102 9.32797 17.6202 9.75797 17.6202C10.168 17.6202 10.558 17.4102 10.778 17.0702C11.138 16.6002 18.008 8.41016 18.008 8.41016C18.908 7.49016 17.818 6.68016 17.098 7.38016V7.39016Z"
                      fill="#12B76A"
                    />
                  </svg>
                ) : (
                  <svg
                    className="min-w-6"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="24" height="24" rx="12" fill="#E7E7E6" />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M17.098 7.39016L9.93797 14.3002L8.03797 12.2702C7.68797 11.9402 7.13797 11.9202 6.73797 12.2002C6.34797 12.4902 6.23797 13.0002 6.47797 13.4102L8.72797 17.0702C8.94797 17.4102 9.32797 17.6202 9.75797 17.6202C10.168 17.6202 10.558 17.4102 10.778 17.0702C11.138 16.6002 18.008 8.41016 18.008 8.41016C18.908 7.49016 17.818 6.68016 17.098 7.38016V7.39016Z"
                      fill="#B0B0B0"
                    />
                  </svg>
                )}
                <span className="txt-lg tracking-tight">{feature.label}</span>
              </div>
            ))}
          </div>

          <div className="mb-4 flex-1">
            {(card.pricingCardContent || []).map((component) =>
              component.__typename === 'ComponentComponentsH5' ? (
                <h5 key={component.id}>{component.text}</h5>
              ) : component.__typename === 'ComponentComponentsPricingSlider' ? (
                <PricingSlider
                  key={component.id}
                  label={component.label}
                  period={period}
                  pricing={pricing}
                  currency={currency}
                  convertPrice={convertPrice}
                />
              ) : null
            )}
          </div>

          <div>
            {card.button && (
              <Button
                variant={card.button.variant}
                size={card.button.size}
                asChild={true}
                className={`basis-full xl:basis-auto`}
              >
                <Link href={card.button.url || ''}>
                  <Icon type={card.button.icon} /> {card.button.label}
                </Link>
              </Button>
            )}
          </div>

          <div className="txt-sm text-grey-500 flex h-12 items-center justify-center">{card.note}</div>
        </div>
      </div>
    </div>
  );
};

export const PricingSlider = ({ label, period, pricing, currency, convertPrice }) => {
  const [index, setIndex] = useState([0]);

  const pricingPlanIndex = index[0] || 0;
  const pricingPlan = pricing?.[pricingPlanIndex] || pricing[0];
  const rawPrice = pricingPlan?.value?.[period];
  const price = convertPrice ? convertPrice(rawPrice) : rawPrice;

  return (
    <div className="mb-6">
      <h5>{price != null ? formatCurrency(price, { currency: currency || 'CZK' }) : '-'}</h5>
      <div className="txt-md text-grey-700 mb-8">{`${pricingPlan?.range[0]} - ${pricingPlan?.range[1]} ${label}`}</div>
      <div className="mb-3">
        <Slider min={0} max={pricing.length - 1} value={index} onValueChange={setIndex} step={1} showSteps={true} />
      </div>
      <div className="flex justify-between">
        <span className="txt-sm text-grey-500">{pricing?.[0]?.range?.[0]}</span>
        <span className="txt-sm text-grey-500">{pricing?.[pricing.length - 1]?.range?.[1]}</span>
      </div>
    </div>
  );
};
